import React, { useEffect, useState, useRef, useMemo } from 'react';
import moment from 'moment';

// Hook for gradual number animation
const useAnimatedValue = (targetValue, duration = 800, enabled = true) => {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef(null);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(null);
  const targetValueRef = useRef(targetValue);
  const isFirstRender = useRef(true);

  useEffect(() => {
    targetValueRef.current = targetValue;

    // On first render or if disabled, set immediately without animation
    if (isFirstRender.current || !enabled) {
      setDisplayValue(targetValue);
      isFirstRender.current = false;
      return;
    }

    if (targetValue === displayValue) return;

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValueRef.current + (targetValueRef.current - startValueRef.current) * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValueRef.current);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, enabled]);

  return displayValue;
};

const TimeTotal = ({ item }) => {
  // Calculate total seconds from durations
  const totalSeconds = useMemo(() => {
    if (!item || !item[1] || !Array.isArray(item[1])) return 0;
    
    let tot = 0;
    item[1].forEach((entry) => {
      if (entry && entry.duration) {
        tot = tot + (entry.duration || 0);
      }
    });
    return tot;
  }, [item]);

  // Animate the total seconds value
  const animatedSeconds = useAnimatedValue(totalSeconds, 800, true);

  // Format the animated value as HH:mm:ss
  const formattedTime = moment.utc(animatedSeconds * 1000).format("HH:mm:ss");

  return <span>{formattedTime}</span>;
};

export default TimeTotal;